import React, { FC, useState } from 'react'

import { useRouter } from 'next/navigation'
import { useForm } from 'react-hook-form'

import { StandardButton, Txt, DecorationTitle } from 'src/components/atoms'
import { SettingButtons, UserDetailManagementContentItem } from 'src/components/molecules'
import { ChangePassword, DeleteUserModal, ForceUpdatePassword } from 'src/components/organisms'

import { BaseYup, yup, yupResolver } from 'src/customYup'
import { useAuth, useAuthority, useToast, useGetUsers, useGetUser } from 'src/hooks'
import { Routes } from 'src/routes'
import { InternalUserDetailEntity, ProfileEntity, UpdatePasswordFormEntity } from 'src/types'

const schema: yup.SchemaOf<UpdatePasswordFormEntity> = BaseYup.object({
  oldPassword: BaseYup.string().required(),
  newPassword: BaseYup.string().min(8, '8文字以上の英数字で入力してください').required(),
  newPasswordConfirm: BaseYup.string()
    .min(8, '8文字以上の英数字で入力してください')
    .required()
    .oneOf([BaseYup.ref('newPassword'), null], '新しいパスワードが一致しません')
    .required('入力して下さい'),
})

type Props = {
  user: InternalUserDetailEntity | ProfileEntity
  className?: string
}

export const InternalUserManagement: FC<Props> = ({ user, className }) => {
  const [isEditMode, setIsEditMode] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const { updatePassword } = useAuth()
  const { canDeleteUser, canManageUser, isSignInUser } = useAuthority()
  const { displayStandardTextToast, displayErrorTextToast } = useToast()
  const { refetch } = useGetUsers()
  const { refetch: refetchUser } = useGetUser({ userId: user.id })

  const router = useRouter()

  const {
    register,
    watch,
    reset,
    trigger,
    formState: { errors },
  } = useForm<UpdatePasswordFormEntity>({
    resolver: yupResolver(schema),
  })

  const afterDelete = async () => {
    displayStandardTextToast('ユーザーを削除しました')
    await refetch()
    router.replace(Routes.UserList.path)
  }

  const handleFinishEdit = async () => {
    await trigger()

    if (
      // formのエラーが存在する場合
      Object.keys(errors).length > 0 ||
      // もしくは確認用パスワードが一致しない場合
      watch('newPassword') !== watch('newPasswordConfirm')
    ) {
      // 処理を行わない
      return
    }

    setIsUpdating(true)

    const formEntity: UpdatePasswordFormEntity = {
      oldPassword: watch('oldPassword'),
      newPassword: watch('newPassword'),
      newPasswordConfirm: watch('newPasswordConfirm'),
    }
    try {
      await updatePassword({ formEntity })
      displayStandardTextToast('パスワードを変更しました')
      setIsEditMode(false)
      reset()
    } catch {
      displayErrorTextToast('パスワードの変更に失敗しました')
    } finally {
      setIsUpdating(false)
    }
  }

  return (
    <div className={className}>
      <div className="tw-full-justify">
        <DecorationTitle>ユーザー管理</DecorationTitle>
        {isSignInUser({ userId: user.id }) && (
          <SettingButtons
            isEditMode={isEditMode}
            isLoading={isUpdating}
            updateIsEditMode={setIsEditMode}
            enabledCheckButton={
              !!watch('oldPassword') && !!watch('newPassword') && !!watch('newPasswordConfirm')
            }
            finishEdit={handleFinishEdit}
          />
        )}
      </div>
      <div className="tw-mt-s1">
        <ul className="tw-grid tw-gap-s2">
          <li>
            <UserDetailManagementContentItem title="本パスワード">
              <Txt sizeType="l">{user.isResetPassword ? '設定済み' : '仮パスワード'}</Txt>
            </UserDetailManagementContentItem>
          </li>

          {isSignInUser({ userId: user.id }) && (
            <li>
              <UserDetailManagementContentItem title="パスワード">
                {isEditMode ? (
                  <ChangePassword register={register} formErrors={errors} />
                ) : (
                  <Txt sizeType="l">******</Txt>
                )}
              </UserDetailManagementContentItem>
            </li>
          )}
        </ul>
      </div>
      {!isEditMode && (
        <>
          {!isSignInUser({ userId: user.id }) && canManageUser && (
            <ForceUpdatePassword
              user={user}
              afterUpdatePassword={refetchUser}
              className="tw-mt-s1"
            />
          )}
          {canDeleteUser({ targetUser: user }) && (
            <div className="tw-mt-s2">
              <StandardButton
                text="ユーザー削除"
                buttonType="danger"
                appendIconPath="trash/soft/line"
                onClick={() => setShowDeleteModal(true)}
                className="tw-ml-auto"
              />
            </div>
          )}
        </>
      )}

      {showDeleteModal && (
        <DeleteUserModal
          users={[user]}
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          afterDelete={afterDelete}
        />
      )}
    </div>
  )
}
